import { useMemo } from 'react'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { useBrandPlanSchedules } from 'hooks/queries/schedules/useBrandPlanSchedules'
import { collectResults } from 'utils/query'
import { calculateChannelCounts } from 'screens/Calendar/WeekViewV2/BatchContentActions/Modals/AddMoreContentModal/utils'
import { calculateCreditCounts } from 'utils/calculateCreditCounts'
import { BrandPlanSchedule, BrandPlanScheduleListItem } from 'types'

dayjs.extend(isoWeek)
dayjs.extend(isSameOrBefore)

interface ScheduleCounts {
  socialCount: number
  blogAndEmailCount: number
  blogCount: number
  emailCount: number
  shortFormVideoCount: number
}

export const useCurrentSchedule = () => {
  const { data: brandPlan } = useCurrentBrandPlan()

  const schedulesQuery = useBrandPlanSchedules(
    {
      brandPlanId: brandPlan?.id || 0,
      start_date: dayjs().startOf('week').format('YYYY-MM-DD'),
      order_direction: 'asc',
    },
    {
      enabled: !!brandPlan?.id,
    },
  )

  const schedules = collectResults(schedulesQuery, (data) => data.schedules)

  return useMemo(() => {
    if (!schedules.length) return

    const now = dayjs()
    const startOfWeek = now.startOf('isoWeek') // Monday
    const endOfWeek = now.endOf('isoWeek') // Sunday

    // First, try to find a schedule that contains the current week
    const currentWeekSchedule = schedules.find((schedule) => {
      if (!schedule.start_date || !schedule.end_date) return false

      const scheduleStart = dayjs(schedule.start_date)
      const scheduleEnd = dayjs(schedule.end_date)

      // Check if dates are valid
      if (!scheduleStart.isValid() || !scheduleEnd.isValid()) return false

      // Check if current week overlaps with schedule
      return scheduleStart.isSameOrBefore(endOfWeek, 'day') && startOfWeek.isSameOrBefore(scheduleEnd, 'day')
    })

    if (currentWeekSchedule) {
      return currentWeekSchedule
    }

    // If no current week schedule, find the next upcoming schedule
    return schedules.find((schedule) => {
      if (!schedule.start_date) return false

      const scheduleStart = dayjs(schedule.start_date)
      return scheduleStart.isValid() && scheduleStart.isAfter(endOfWeek, 'day')
    })
  }, [schedules])
}

export const useScheduleCounts = (schedule?: BrandPlanSchedule | BrandPlanScheduleListItem): ScheduleCounts => {
  const channelCounts = useMemo(() => {
    if (!schedule) return undefined

    return calculateChannelCounts(schedule)
  }, [schedule])

  const creditCounts = useMemo(() => {
    return calculateCreditCounts(channelCounts, undefined)
  }, [channelCounts])

  return {
    socialCount: creditCounts.social,
    blogAndEmailCount: creditCounts.blogAndEmail,
    blogCount: creditCounts.blog,
    emailCount: creditCounts.email,
    shortFormVideoCount: creditCounts.shortFormVideo,
  }
}

export const useCurrentScheduleCounts = (): ScheduleCounts => {
  const currentSchedule = useCurrentSchedule()

  return useScheduleCounts(currentSchedule)
}
